import { useEffect, useMemo, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  ResponsiveContainer, LineChart as RLineChart, Line, XAxis, YAxis, Tooltip as RTooltip,
  CartesianGrid, Legend,
} from "recharts";
import { api, fmtUsd, fmtPct, fmtTime } from "@/lib/api";
import { SectionTitle, Badge } from "@/components/Bits";
import { StrategySummary } from "@/components/StrategyPicker";

// Pretty key labels for config diff
const CFG_KEYS = [
  "strategy_id", "start_iso", "end_iso", "initial_balance", "leverage",
  "order_size_mode", "order_size_value", "hedge_mode", "fee_rate",
  "slippage_bps", "liquidations_mode",
];

// Metrics we put side-by-side
const METRICS = [
  { k: "total_pnl_usdt", label: "Total PnL", fmt: (v) => `${v >= 0 ? "+" : ""}$${fmtUsd(v)}`, isPos: true },
  { k: "total_pnl_percent", label: "Total PnL %", fmt: (v) => fmtPct(v), isPos: true },
  { k: "total_trades", label: "Trades", fmt: (v) => `${v}` },
  { k: "long_trades", label: "Long Trades", fmt: (v) => `${v}` },
  { k: "short_trades", label: "Short Trades", fmt: (v) => `${v}` },
  { k: "win_rate", label: "Win Rate", fmt: (v) => fmtPct(v, 1), isPos: true },
  { k: "profit_factor", label: "Profit Factor", fmt: (v) => Number(v).toFixed(2), isPos: true },
  { k: "max_drawdown_percent", label: "Max Drawdown", fmt: (v) => `-${fmtPct(v, 2).replace("+", "")}`, isNeg: true },
  { k: "max_drawdown_usdt", label: "Max DD $", fmt: (v) => `-$${fmtUsd(v)}`, isNeg: true },
  { k: "avg_win_usdt", label: "Avg Win", fmt: (v) => `$${fmtUsd(v)}` },
  { k: "avg_loss_usdt", label: "Avg Loss", fmt: (v) => `$${fmtUsd(v)}` },
  { k: "best_trade_usdt", label: "Best Trade", fmt: (v) => `$${fmtUsd(v)}` },
  { k: "worst_trade_usdt", label: "Worst Trade", fmt: (v) => `$${fmtUsd(v)}` },
  { k: "avg_trade_duration_minutes", label: "Avg Duration (m)", fmt: (v) => Number(v || 0).toFixed(1) },
  { k: "total_fees_usdt", label: "Total Fees", fmt: (v) => `$${fmtUsd(v)}` },
  { k: "final_equity", label: "Final Equity", fmt: (v) => `$${fmtUsd(v)}` },
];

function deltaClass(metricKey, a, b) {
  if (a == null || b == null) return "text-[#A0A0AB]";
  const m = METRICS.find((mm) => mm.k === metricKey);
  const diff = b - a;
  if (Math.abs(diff) < 1e-9) return "text-[#A0A0AB]";
  if (m?.isPos) return diff > 0 ? "long-text" : "short-text";
  if (m?.isNeg) return diff > 0 ? "short-text" : "long-text"; // smaller drawdown is better
  return "text-[#A0A0AB]";
}

function renderDelta(metricKey, a, b) {
  if (a == null || b == null) return "—";
  const diff = b - a;
  if (Math.abs(diff) < 1e-9) return "0";
  const m = METRICS.find((mm) => mm.k === metricKey);
  // Use the metric's formatter for the delta where it makes sense
  if (m?.k.endsWith("_percent") || m?.k === "win_rate") return `${diff >= 0 ? "+" : ""}${diff.toFixed(2)}%`;
  if (m?.k === "profit_factor") return `${diff >= 0 ? "+" : ""}${diff.toFixed(2)}`;
  if (typeof diff === "number" && Number.isFinite(diff) && Math.abs(diff) >= 1) {
    return `${diff >= 0 ? "+" : ""}$${fmtUsd(diff)}`;
  }
  return `${diff >= 0 ? "+" : ""}${diff.toFixed(4)}`;
}

export default function CompareBacktests() {
  const [list, setList] = useState([]);
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");
  const [aDoc, setADoc] = useState(null);
  const [bDoc, setBDoc] = useState(null);

  useEffect(() => { (async () => setList((await api.backtestList()) || []))(); }, []);

  useEffect(() => { (async () => { if (aId) setADoc(await api.backtestGet(aId, true)); else setADoc(null); })(); }, [aId]);
  useEffect(() => { (async () => { if (bId) setBDoc(await api.backtestGet(bId, true)); else setBDoc(null); })(); }, [bId]);

  const completed = list.filter((b) => b.status === "completed");

  // Merge equity curves into one chart series
  const equityChart = useMemo(() => {
    if (!aDoc?.equity_curve && !bDoc?.equity_curve) return [];
    // Normalize x-axis to candle index so different time ranges line up
    const a = (aDoc?.equity_curve || []).map((p, i) => ({ idx: i, A: p.equity }));
    const b = (bDoc?.equity_curve || []).map((p, i) => ({ idx: i, B: p.equity }));
    const len = Math.max(a.length, b.length);
    const out = [];
    for (let i = 0; i < len; i++) {
      out.push({ idx: i, A: a[i]?.A ?? null, B: b[i]?.B ?? null });
    }
    return out;
  }, [aDoc, bDoc]);

  const configDiff = useMemo(() => {
    if (!aDoc?.config || !bDoc?.config) return [];
    const out = [];
    for (const k of CFG_KEYS) {
      const av = aDoc.config?.[k];
      const bv = bDoc.config?.[k];
      if (JSON.stringify(av) !== JSON.stringify(bv)) {
        out.push({ key: k, a: av, b: bv });
      }
    }
    return out;
  }, [aDoc, bDoc]);

  return (
    <div className="p-6 space-y-6">
      <SectionTitle sub="Pick two completed backtests and see KPIs side-by-side. Useful for parameter changes (RSI period, TP%, leverage, etc).">
        Compare Backtests
      </SectionTitle>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[{ side: "A", id: aId, set: setAId, doc: aDoc }, { side: "B", id: bId, set: setBId, doc: bDoc }].map((slot) => (
          <div key={slot.side} className="bg-[#0F0F14] border border-border rounded-sm p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge kind={slot.side === "A" ? "on" : "warn"} testid={`compare-${slot.side.toLowerCase()}-badge`}>{slot.side}</Badge>
                <Label className="text-xs">Backtest {slot.side}</Label>
              </div>
              {slot.doc ? (
                <a
                  data-testid={`compare-csv-${slot.side.toLowerCase()}`}
                  href={api.backtestCsvUrl(slot.doc.id)}
                  download
                  className="text-xs text-[#A0A0AB] underline hover:text-white"
                >
                  CSV
                </a>
              ) : null}
            </div>
            <Select value={slot.id} onValueChange={slot.set}>
              <SelectTrigger data-testid={`compare-select-${slot.side.toLowerCase()}`} className="bg-[#1A1A22] border-border rounded-sm">
                <SelectValue placeholder={completed.length ? "Choose a backtest" : "No completed backtests yet"} />
              </SelectTrigger>
              <SelectContent>
                {completed.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {(b.strategy_config_snapshot?.name || "—")} · {b.resolved_timeframe || b.strategy_config_snapshot?.timeframe || "—"} · {b.config?.start_iso?.slice(0, 10)} → {b.config?.end_iso?.slice(0, 10)} · {b.metrics?.total_trades ?? 0} trades · {b.id.slice(0, 8)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {slot.doc ? (
              <div className="space-y-3">
                <div className="text-xs font-mono text-[#A0A0AB]">
                  Created: {fmtTime(slot.doc.created_at)} · Status:{" "}
                  <span className="text-white">{slot.doc.status}</span>
                </div>
                {slot.doc.strategy_config_snapshot ? (
                  <StrategySummary profile={slot.doc.strategy_config_snapshot} />
                ) : null}
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {aDoc && bDoc ? (
        <>
          {/* Side-by-side KPIs */}
          <div className="bg-[#0F0F14] border border-border rounded-sm overflow-x-auto">
            <table className="w-full text-sm" data-testid="compare-kpi-table">
              <thead className="text-[10px] uppercase tracking-widest text-[#71717A]">
                <tr>
                  <th className="text-left px-4 py-3">Metric</th>
                  <th className="text-right px-4 py-3">A</th>
                  <th className="text-right px-4 py-3">B</th>
                  <th className="text-right px-4 py-3">Δ (B − A)</th>
                </tr>
              </thead>
              <tbody>
                {METRICS.map((m) => {
                  const av = aDoc.metrics?.[m.k];
                  const bv = bDoc.metrics?.[m.k];
                  return (
                    <tr key={m.k} className="border-t border-border/60">
                      <td className="px-4 py-2 text-[#A0A0AB]">{m.label}</td>
                      <td className="px-4 py-2 cell-num">{av == null ? "—" : m.fmt(av)}</td>
                      <td className="px-4 py-2 cell-num">{bv == null ? "—" : m.fmt(bv)}</td>
                      <td className={`px-4 py-2 cell-num ${deltaClass(m.k, av, bv)}`} data-testid={`compare-delta-${m.k}`}>
                        {renderDelta(m.k, av, bv)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Config diff */}
          <div className="bg-[#0F0F14] border border-border rounded-sm">
            <div className="px-4 py-3 border-b border-border text-xs uppercase tracking-widest text-[#A0A0AB]">
              Config differences
            </div>
            <div className="p-4">
              {configDiff.length === 0 ? (
                <div className="text-sm text-[#71717A]">Configs are identical for the listed keys.</div>
              ) : (
                <table className="w-full text-sm" data-testid="compare-config-diff">
                  <thead className="text-[10px] uppercase tracking-widest text-[#71717A]">
                    <tr>
                      <th className="text-left pb-2">Key</th>
                      <th className="text-left pb-2">A</th>
                      <th className="text-left pb-2">B</th>
                    </tr>
                  </thead>
                  <tbody>
                    {configDiff.map((d) => (
                      <tr key={d.key} className="border-t border-border/60">
                        <td className="py-2 font-mono">{d.key}</td>
                        <td className="py-2 font-mono text-[#A0A0AB]">{String(d.a)}</td>
                        <td className="py-2 font-mono text-white">{String(d.b)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Equity curves overlay */}
          <div className="bg-[#0F0F14] border border-border rounded-sm p-4">
            <div className="text-xs uppercase tracking-widest text-[#A0A0AB] mb-3">Equity curves (X-axis: candle index)</div>
            <div className="h-72" data-testid="compare-equity-chart">
              <ResponsiveContainer width="100%" height="100%">
                <RLineChart data={equityChart} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis dataKey="idx" tick={{ fill: "#71717A", fontSize: 10 }} stroke="#27272A" minTickGap={50} />
                  <YAxis tick={{ fill: "#71717A", fontSize: 10 }} stroke="#27272A" domain={["dataMin", "dataMax"]} />
                  <RTooltip contentStyle={{ background: "#0F0F14", border: "1px solid #27272A", borderRadius: 2, fontSize: 12 }} labelStyle={{ color: "#A0A0AB" }} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "#A0A0AB" }} />
                  <Line type="monotone" dataKey="A" stroke="#00E676" strokeWidth={1.5} dot={false} connectNulls isAnimationActive={false} />
                  <Line type="monotone" dataKey="B" stroke="#3B82F6" strokeWidth={1.5} dot={false} connectNulls isAnimationActive={false} />
                </RLineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      ) : (
        <div className="bg-[#0F0F14] border border-border rounded-sm p-6 text-sm text-[#71717A]">
          Select two completed backtests above to see the comparison.
        </div>
      )}
    </div>
  );
}
